import { memo, useCallback, useMemo, useState } from 'react';
import Panel from '@/features/report/shared/Panel/Panel';
import type { Tag as TagModel } from '@/models/tag';
import { Tag } from '@/shared/ui/Tag';
import { TagChipRow } from './TagChip';
import { TagCombobox } from './TagCombobox';
import { normalizeTagInput } from './utils/normalizeTagInput';
import styles from './TagsPanel.module.css';

interface TagsPanelProps {
  catalog: string[];
  value: TagModel[];
  onChange: (tags: TagModel[]) => void;
  disabled?: boolean;
}

function TagsPanel({
  catalog,
  value,
  onChange,
  disabled = false,
}: TagsPanelProps) {
  const [draft, setDraft] = useState('');

  const selectedSet = useMemo(
    () => new Set(value.map((tag) => tag.value)),
    [value]
  );
  const needle = useMemo(() => normalizeTagInput(draft), [draft]);

  const suggestions = useMemo(() => {
    const pool = catalog.filter((entry) => !selectedSet.has(entry));
    if (!needle) return pool;
    return pool.filter((entry) => entry.includes(needle));
  }, [catalog, needle, selectedSet]);

  // Offer a "Create" row when the typed value is neither selected nor in the
  // catalog (so it isn't already offered as a suggestion).
  const createValue =
    needle && !selectedSet.has(needle) && !catalog.includes(needle)
      ? needle
      : null;

  const commit = useCallback(
    (text: string) => {
      const normalized = normalizeTagInput(text);
      if (normalized === null) return;
      if (selectedSet.has(normalized)) {
        setDraft('');
        return;
      }
      onChange([...value, { value: normalized, source: 'MANUAL' }]);
      setDraft('');
    },
    [onChange, selectedSet, value]
  );

  const handleRemove = useCallback(
    (removed: string) => {
      onChange(value.filter((tag) => tag.value !== removed));
    },
    [onChange, value]
  );

  const handleRemoveLast = useCallback(() => {
    if (value.length === 0) return;
    onChange(value.slice(0, -1));
  }, [onChange, value]);

  const isEmpty = value.length === 0;

  return (
    <Panel title="Tags">
      <div className={styles.container}>
        {!isEmpty && (
          <TagChipRow>
            {value.map((tag) => (
              <Tag
                key={tag.value}
                value={tag.value}
                source={tag.source}
                disabled={disabled}
                onRemove={handleRemove}
                role="listitem"
              />
            ))}
          </TagChipRow>
        )}
        {isEmpty && !disabled && (
          <p className={styles.emptyHint}>
            No tags yet — type or pick from suggestions
          </p>
        )}
        <TagCombobox
          draft={draft}
          onDraftChange={setDraft}
          suggestions={suggestions}
          onCommit={commit}
          createValue={createValue}
          onRemoveLast={handleRemoveLast}
          onClearDraft={() => setDraft('')}
          disabled={disabled}
        />
      </div>
    </Panel>
  );
}

export default memo(TagsPanel);
